// src/components/cobranza/ProntoPagoPage.js
// Módulo "Bonificación por Pronto Pago" — SOLO LECTURA (empresa Tecno).
// Pinta el resultado del job nocturno (Talend) que vive en la UDT SAP @NC_PP_DIAG.
// No calcula reglas ni crea NC: el botón "Ejecutar ahora" solo informa que la
// generación corre al cierre del día.
//
// Los colores semánticos (estatus / condición / tipo) van en hex fijos a propósito,
// para que el look coincida con el mockup aprobado; no son tokens de marca.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Tag, Tooltip, Input, Segmented, Button,
    Alert, Empty, Skeleton, Statistic, Space,
} from 'antd';
import {
    CheckCircleFilled, CloseCircleFilled, ClockCircleFilled,
} from '@ant-design/icons';
import { prontoPagoService } from '../../services/api';

// ── Paleta fija del artifact (§8.1) ──────────────────────────────────────────
const C = {
    text: '#1b2430', muted: '#6c7787', bonif: '#0a6e4e', dash: '#b9c0c8',
    okText: '#0f9d6e', okBg: '#e7f6ef',
    noText: '#d4504a', noBg: '#fbeceb',
    waitText: '#bb7a14', waitBg: '#fbf0db',
    contadoText: '#1d4f9c', contadoBg: '#e8f0fc',
    creditoText: '#0a6e4e', creditoBg: '#e7f6ef',
};

// Tags de estatus: texto / fondo / etiqueta legible.
const ESTATUS_CFG = {
    PENDIENTE:   { text: '#0f9d6e', bg: '#e7f6ef', label: 'Pendiente' },
    POR_EVALUAR: { text: '#bb7a14', bg: '#fbf0db', label: 'Por evaluar' },
    OMITIDO:     { text: '#6a5bd6', bg: '#f3eef7', label: 'Omitido' },
    NO_ELEGIBLE: { text: '#d4504a', bg: '#fbeceb', label: 'No elegible' },
    NO_APLICA:   { text: '#6c7787', bg: '#eef0f2', label: 'No aplica' },
    NC_CREADA:   { text: '#1d4f9c', bg: '#eef4fd', label: 'NC creada' },
};

// Estatus en los que las condiciones no aplican (se muestran con "—").
const SIN_CONDICIONES = new Set(['NO_APLICA', 'OMITIDO']);

// Opciones del filtro Segmented. value '' = Todos.
const FILTROS = [
    { value: '', label: 'Todos', countKey: 'total' },
    { value: 'PENDIENTE', label: 'Pendientes', countKey: 'PENDIENTE' },
    { value: 'POR_EVALUAR', label: 'Por evaluar', countKey: 'POR_EVALUAR' },
    { value: 'OMITIDO', label: 'Omitidos', countKey: 'OMITIDO' },
    { value: 'NO_ELEGIBLE', label: 'No elegibles', countKey: 'NO_ELEGIBLE' },
    { value: 'NO_APLICA', label: 'No aplican', countKey: 'NO_APLICA' },
    { value: 'NC_CREADA', label: 'NC creadas', countKey: 'NC_CREADA' },
];

const mxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const fmtMoney = (v) => (v == null || isNaN(v)) ? '—' : mxn.format(Number(v));
const fmtMoneyInt = (v) => (v == null || isNaN(v))
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(v));
// OJO zona horaria: las fechas de SAP vienen como string date-only (YYYY-MM-DD).
// NO usar `new Date(s)` — lo interpreta como medianoche UTC y en México (UTC-6)
// retrocede un día (19/06 → 18/06). Parseamos el string directo, sin Date.
const fmtDdMm = (s) => {
    if (!s) return '—';
    const [, m, d] = String(s).substring(0, 10).split('-');
    if (!d || !m) return '—';
    return `${d}/${m}`;
};
const fmtFullDate = (s) => {
    if (!s) return null;
    const [a, m, d] = String(s).substring(0, 10).split('-');
    if (!a || !m || !d) return null;
    return `${d}/${m}/${a}`;
};

// ── Celda de condición reutilizable: círculo 20px + icono + detalle debajo ────
const Cond = ({ state, detalle }) => {
    const map = {
        ok:   { Icon: CheckCircleFilled, color: C.okText, bg: C.okBg },
        no:   { Icon: CloseCircleFilled, color: C.noText, bg: C.noBg },
        wait: { Icon: ClockCircleFilled, color: C.waitText, bg: C.waitBg },
    };
    const { Icon, color, bg } = map[state] || map.no;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{
                width: 20, height: 20, borderRadius: '50%', background: bg,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon style={{ color, fontSize: 13 }} />
            </span>
            {detalle != null && (
                <span style={{ fontSize: 10.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{detalle}</span>
            )}
        </div>
    );
};

const Dash = () => <span style={{ color: C.dash }}>—</span>;

const ProntoPagoPage = () => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [resumen, setResumen] = useState(null);
    const [resumenLoading, setResumenLoading] = useState(false);

    const [estatus, setEstatus] = useState('');
    const [q, setQ] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });

    const fetchResumen = useCallback(async () => {
        setResumenLoading(true);
        try {
            const res = await prontoPagoService.getResumen();
            setResumen(res.data);
        } catch (e) {
            // El resumen es secundario: si falla, no bloqueamos la tabla.
            console.error('Error al cargar resumen de pronto pago:', e);
        } finally {
            setResumenLoading(false);
        }
    }, []);

    const fetchDocs = useCallback(async (override = null) => {
        setLoading(true);
        setError(null);
        const targetPage = override?.current ?? pagination.current;
        const targetPageSize = override?.pageSize ?? pagination.pageSize;
        const targetEstatus = override?.estatus !== undefined ? override.estatus : estatus;
        const targetQ = override?.q !== undefined ? override.q : q;
        try {
            const params = { page: targetPage, pageSize: targetPageSize };
            if (targetEstatus) params.estatus = targetEstatus;
            if (targetQ) params.q = targetQ;
            const res = await prontoPagoService.getDocumentos(params);
            setDocs(res.data.documentos || []);
            setPagination({ current: res.data.page || targetPage, pageSize: res.data.pageSize || targetPageSize, total: res.data.total || 0 });
        } catch (e) {
            const msg = e.response?.data?.message || e.message || 'Ocurrió un error al consultar los documentos.';
            setError(msg);
            setDocs([]);
            setPagination((prev) => ({ ...prev, total: 0 }));
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, pagination.pageSize, estatus, q]);

    useEffect(() => {
        fetchResumen();
        fetchDocs({ current: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFiltro = (value) => {
        setEstatus(value);
        fetchDocs({ current: 1, estatus: value });
    };

    const handleSearch = (value) => {
        const v = (value || '').trim();
        setQ(v);
        fetchDocs({ current: 1, q: v });
    };

    // ── Columnas (§8.6) ──────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Factura · Cliente · Montos',
            key: 'factura',
            width: '26%',
            render: (_, r) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ fontWeight: 600, color: C.text }}>
                        {r.factura}
                        {r.agente && (
                            <span style={{ fontWeight: 400, fontSize: 11.5, color: C.muted, marginLeft: 8 }}>
                                · {r.agente}
                            </span>
                        )}
                    </span>
                    <span style={{ fontSize: 12, color: C.muted, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.cliente}
                    </span>
                    <span style={{ fontSize: 11.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: C.muted }}>Factura</span> {fmtMoney(r.montoFactura)}
                    </span>
                    <span style={{ fontSize: 11.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: C.muted }}>Pago</span> {r.pagoAplicado == null ? '—' : fmtMoney(r.pagoAplicado)}
                    </span>
                </div>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo',
            key: 'tipo',
            width: '8%',
            render: (tipo) => {
                if (!tipo) return <Dash />;
                const isContado = tipo === 'CONTADO';
                return (
                    <Tag style={{
                        color: isContado ? C.contadoText : C.creditoText,
                        background: isContado ? C.contadoBg : C.creditoBg,
                        border: 'none', borderRadius: 8,
                    }}>
                        {isContado ? 'Contado' : 'Crédito'}
                    </Tag>
                );
            },
        },
        {
            title: 'Fecha pago',
            dataIndex: 'fechaPago',
            key: 'fechaPago',
            width: '9%',
            render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDdMm(v)}</span>,
        },
        {
            title: 'Días háb. (≤3)',
            key: 'diasHabiles',
            width: '11%',
            align: 'center',
            render: (_, r) => {
                if (SIN_CONDICIONES.has(r.estatus)) return <Dash />;
                if (r.diasHabiles == null) return <Cond state="wait" detalle="sin arribo" />;
                return <Cond state={r.cumplePlazo ? 'ok' : 'no'} detalle={`${r.diasHabiles} d`} />;
            },
        },
        {
            title: 'Suma facturas del pago',
            key: 'sumaFacturasPago',
            width: '14%',
            align: 'center',
            render: (_, r) => {
                if (SIN_CONDICIONES.has(r.estatus)) return <Dash />;
                // Gate evaluado a nivel línea: cada fila muestra su propio umbral (U_Gate).
                const detalle = (
                    <>
                        <span style={{ display: 'block' }}>{fmtMoneyInt(r.sumaFacturasPago)}</span>
                        {r.gate != null && (
                            <span style={{ display: 'block', fontSize: 9.5, opacity: 0.85 }}>≥ {fmtMoneyInt(r.gate)}</span>
                        )}
                    </>
                );
                return <Cond state={r.cumpleGate ? 'ok' : 'no'} detalle={detalle} />;
            },
        },
        {
            title: 'Saldo 5%',
            key: 'saldo',
            width: '10%',
            align: 'center',
            render: (_, r) => {
                if (SIN_CONDICIONES.has(r.estatus)) return <Dash />;
                return <Cond state={r.cumpleSaldo ? 'ok' : 'no'} detalle={r.cumpleSaldo ? '= 5%' : '≠ 5%'} />;
            },
        },
        {
            title: 'Bonificación',
            dataIndex: 'bonificacion',
            key: 'bonificacion',
            width: '11%',
            align: 'right',
            render: (v, r) => {
                if (SIN_CONDICIONES.has(r.estatus)) return <Dash />;
                return <span style={{ fontWeight: 600, color: C.bonif, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>;
            },
        },
        {
            title: 'Estatus',
            key: 'estatus',
            width: '11%',
            render: (_, r) => {
                const cfg = ESTATUS_CFG[r.estatus] || { text: C.muted, bg: '#eef0f2', label: r.estatus };
                const tag = (
                    <Tag style={{ color: cfg.text, background: cfg.bg, border: 'none', borderRadius: 8 }}>
                        {cfg.label}
                    </Tag>
                );
                return r.motivo ? <Tooltip title={r.motivo}>{tag}</Tooltip> : tag;
            },
        },
        {
            title: 'Nota de crédito',
            key: 'nc',
            width: '11%',
            render: (_, r) => {
                if (!r.docNumNC) return <Dash />;
                const fecha = fmtFullDate(r.fechaNC);
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>NC {r.docNumNC}</span>
                        <span style={{ fontSize: 11.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                            {fecha || 'sin fecha'}
                        </span>
                    </div>
                );
            },
        },
    ];

    // ── Tarjetas de resumen ────────────────────────────────────────────────────
    const summaryCards = [
        { key: 'PENDIENTE', label: 'Pendientes' },
        { key: 'POR_EVALUAR', label: 'Por evaluar' },
        { key: 'OMITIDO', label: 'Omitidos' },
        { key: 'NO_ELEGIBLE', label: 'No elegibles' },
        { key: 'NO_APLICA', label: 'No aplican' },
    ];

    const cortes = resumen
        ? `${resumen.total} docs · ${fmtMoneyInt(resumen.bonificacionPendiente)} pendientes · las NC se generan al cierre del día`
        : 'Cargando corte…';

    return (
        <div>
            {/* Encabezado */}
            <div style={{ marginBottom: 16 }}>
                <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, color: C.text }}>
                    Bonificación por pronto pago
                </h1>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{cortes}</div>
            </div>

            {/* Tarjetas resumen */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16 }}>
                {summaryCards.map((c) => (
                    <Card key={c.key} size="small" style={{ borderColor: '#e4e8ed', borderRadius: 12 }}>
                        {resumenLoading || !resumen
                            ? <Skeleton active paragraph={false} title={{ width: '60%' }} />
                            : <Statistic title={c.label} value={resumen.porEstatus?.[c.key] ?? 0} />}
                    </Card>
                ))}
                <Card size="small" style={{ borderColor: '#e4e8ed', borderRadius: 12 }}>
                    {resumenLoading || !resumen
                        ? <Skeleton active paragraph={false} title={{ width: '60%' }} />
                        : <Statistic title="Bonif. pendiente" value={resumen.bonificacionPendiente ?? 0}
                            precision={2} prefix="$" valueStyle={{ color: C.bonif }} />}
                </Card>
            </div>

            {/* Filtro + buscador */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                <Segmented
                    value={estatus}
                    onChange={handleFiltro}
                    options={FILTROS.map((f) => ({
                        value: f.value,
                        label: resumen
                            ? `${f.label} (${f.countKey === 'total' ? resumen.total : (resumen.porEstatus?.[f.countKey] ?? 0)})`
                            : f.label,
                    }))}
                />
                <Input.Search
                    placeholder="Buscar factura, cliente…"
                    allowClear
                    onSearch={handleSearch}
                    style={{ maxWidth: 320 }}
                />
            </div>

            {/* Error */}
            {error && (
                <Alert
                    type="error"
                    showIcon
                    message="No se pudieron cargar los documentos"
                    description={error}
                    style={{ marginBottom: 12 }}
                    action={<Button size="small" onClick={() => fetchDocs()}>Reintentar</Button>}
                />
            )}

            {/* Tabla */}
            <Table
                columns={columns}
                dataSource={docs}
                rowKey="factura"
                loading={loading}
                size="middle"
                scroll={{ x: 1120 }}
                locale={{ emptyText: <Empty description="Sin documentos para este filtro" /> }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    showTotal: (t) => `${t} documentos`,
                }}
                onChange={(cfg) => fetchDocs({ current: cfg.current, pageSize: cfg.pageSize })}
            />

            {/* Leyenda y pie */}
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 12, lineHeight: 1.6 }}>
                <Space size={16} wrap>
                    <span><CheckCircleFilled style={{ color: C.okText }} /> cumple</span>
                    <span><CloseCircleFilled style={{ color: C.noText }} /> no cumple</span>
                    <span><ClockCircleFilled style={{ color: C.waitText }} /> pendiente de dato</span>
                </Space>
                <div style={{ marginTop: 4 }}>
                    Factura = total del documento; Pago = monto aplicado del cobro (~95%);
                    Suma facturas del pago = base del gate (suma de totales de factura del mismo cobro).
                </div>
            </div>
        </div>
    );
};

export default ProntoPagoPage;
